import {
  WebSocketEventListener,
  WebSocketEventMap,
  WebSocketEventType,
} from '@/types/websocket';

export class WebSocketManager {
  private ws: WebSocket | null = null;

  private url: string;

  private token: string | null = null;

  private reconnectAttempts = 0;

  private maxReconnectAttempts = 5;

  private reconnectTimeout = 3000;

  private listeners: Map<WebSocketEventType, Set<WebSocketEventListener<any>>> =
    new Map();

  private isConnecting = false;

  private static readonly EVENT_MAP: Record<string, WebSocketEventType> = {
    auth: WebSocketEventType.AUTH,
    'auth:response': WebSocketEventType.AUTH_RESPONSE,
    'chat:message': WebSocketEventType.CHAT_MESSAGE,
    'chat:typing': WebSocketEventType.CHAT_TYPING,
    'chat:read': WebSocketEventType.CHAT_READ,
    'chat:history': WebSocketEventType.CHAT_HISTORY,
    'chat:fetch_history': WebSocketEventType.CHAT_FETCH_HISTORY,
    'chat:conversations': WebSocketEventType.CHAT_CONVERSATIONS,
  };

  constructor(url: string) {
    this.url = url;
  }

  public setToken(token: string | null) {
    this.token = token;
    if (token) {
      this.connect();
    } else {
      this.disconnect();
    }
  }

  public connect(): void {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;
    console.log('Connecting to WebSocket...');

    try {
      this.ws = new WebSocket(this.url);
      this.setupWebSocketHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.handleReconnect();
    }
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
  }

  public on<T extends WebSocketEventType>(
    event: T | string,
    listener: WebSocketEventListener<T>,
  ): () => void {
    // Convert string event type to enum if needed
    const eventType = (
      typeof event === 'string'
        ? Object.values(WebSocketEventType).find((type) => type === event)
        : event
    ) as WebSocketEventType;

    if (!eventType) {
      console.warn('[WebSocketManager] Unknown event type:', event);
      return () => {}; // Return no-op unsubscribe function for unknown events
    }

    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    // Debug log the current state of listeners
    console.log(
      '[WebSocketManager] Current listeners:',
      Array.from(this.listeners.keys()).map((k) => k.toString()),
    );

    console.log('[WebSocketManager] Adding listener for event:', eventType);

    this.listeners.get(eventType)?.add(listener);

    // Return unsubscribe function
    return () => {
      console.log('[WebSocketManager] Removing listener for event:', eventType);
      this.listeners.get(eventType)?.delete(listener);
    };
  }

  public emit<T extends WebSocketEventType>(
    event: T,
    payload: WebSocketEventMap[T],
  ): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn(
        '[WebSocketManager] WebSocket is not connected. Message not sent:',
        {
          event,
          payload,
        },
      );
      return;
    }

    try {
      this.ws.send(JSON.stringify({ event, data: payload }));
    } catch (error) {
      console.error('[WebSocketManager] Failed to send message:', error);
    }
  }

  private static mapBackendEventType(
    backendEvent: string,
  ): WebSocketEventType | null {
    // Map the backend event string to the enum value
    const eventType = Object.values(WebSocketEventType).find(
      (type) => type === backendEvent,
    );

    if (!eventType) {
      console.warn('[WebSocketManager] Unknown event type:', backendEvent);
      return null;
    }

    return eventType;
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('[WebSocketManager] WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      // Send authentication if token exists
      if (this.token) {
        this.emit(WebSocketEventType.AUTH, { token: this.token });
      }

      this.notifyListeners(WebSocketEventType.CONNECT, undefined);
    };

    this.ws.onclose = () => {
      console.log('[WebSocketManager] WebSocket closed');
      this.notifyListeners(WebSocketEventType.DISCONNECT, undefined);
      this.handleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocketManager] WebSocket error:', error);
      this.notifyListeners(WebSocketEventType.ERROR, {
        code: 'CONNECTION_ERROR',
        message: 'WebSocket connection error',
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const { event: eventType, data } = JSON.parse(event.data);
        console.log(
          '[WebSocketManager] Received event:',
          eventType,
          'with data:',
          data,
        );

        // Convert backend event type to frontend event type
        const frontendEventType =
          WebSocketManager.mapBackendEventType(eventType);
        if (frontendEventType) {
          console.log(
            '[WebSocketManager] Processing event:',
            frontendEventType,
          );
          this.notifyListeners(frontendEventType, data);
        } else {
          console.warn('[WebSocketManager] Unknown event type:', eventType);
        }
      } catch (error) {
        console.error(
          '[WebSocketManager] Failed to parse WebSocket message:',
          error,
        );
      }
    };
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.token) {
      this.reconnectAttempts += 1;
      console.log(
        `[WebSocketManager] Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`,
      );

      setTimeout(() => {
        this.connect();
      }, this.reconnectTimeout * this.reconnectAttempts);
    } else {
      console.log(
        '[WebSocketManager] Max reconnection attempts reached or no token available',
      );
      this.isConnecting = false;
    }
  }

  private notifyListeners<T extends WebSocketEventType>(
    event: T,
    payload: WebSocketEventMap[T],
  ): void {
    // Debug log the current state of listeners
    console.log(
      '[WebSocketManager] Current listeners before notification:',
      Array.from(this.listeners.keys()).map((k) => k.toString()),
      'Looking for event:',
      event,
    );

    const eventListeners = this.listeners.get(event);
    if (!eventListeners || eventListeners.size === 0) {
      console.warn('[WebSocketManager] No listeners found for event:', event);
      return;
    }

    console.log(
      '[WebSocketManager] Notifying',
      eventListeners.size,
      'listeners for event:',
      event,
    );

    eventListeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error('[WebSocketManager] Error in event listener:', error);
      }
    });
  }
}
